import React, { useEffect, useState } from 'react';
import { useVoiceChat } from '../hooks/useVoiceChat';

const VoiceChat = ({ dialogId, settingsId, coachName, coachAvatar }) => {
    const {
        isConnected,
        isRecording,
        isSpeaking,
        transcript,
        assistantText,
        messages,
        startRecording,
        stopRecording,
        stopPlayback,
        error
    } = useVoiceChat(dialogId, settingsId);

    const [localTranscript, setLocalTranscript] = useState('');
    const messagesEndRef = React.useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, localTranscript, assistantText]);

    useEffect(() => {
        setLocalTranscript(transcript);
    }, [transcript]);

    const toggleRecording = () => {
        if (isRecording) {
            stopRecording();
        } else {
            startRecording();
        }
    };

    return (
        <div className="bg-[var(--surface-white)] rounded-[2.5rem] shadow-xl shadow-black/5 border border-[var(--border-light)] p-8 md:p-12 flex flex-col items-center justify-center min-h-[500px] w-full">

            {/* Avatar & Status */}
            <div className="relative mb-8">
                <div className={`w-32 h-32 rounded-full overflow-hidden border-4 transition-all duration-300 ${isSpeaking ? 'border-accent-green shadow-[0_0_20px_rgba(74,222,128,0.5)] scale-105' : 'border-[var(--border-light)]'
                    }`}>
                    <img
                        src={coachAvatar}
                        alt={coachName}
                        className="w-full h-full object-cover"
                    />
                </div>

                {/* Connection Status Badge */}
                <div className={`absolute bottom-0 right-0 w-8 h-8 rounded-full flex items-center justify-center ${isConnected ? 'bg-green-500' : 'bg-red-500/100'
                    }`} title={isConnected ? "Connected" : "Disconnected"}>
                    {isConnected && <div className="w-3 h-3 bg-white rounded-full animate-pulse" />}
                </div>
            </div>

            <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-2">{coachName}</h2>
            <p className="text-[var(--text-secondary)] mb-8">{isSpeaking ? 'Speaking...' : isRecording ? 'Listening...' : 'Ready to chat'}</p>

            {/* Transcript Area */}
            <div className="w-full max-w-md space-y-4 mb-8 flex-grow flex flex-col px-2 sm:px-0">
                {/* Unified Conversation View */}
                <div className="w-full max-h-96 overflow-y-auto space-y-4 pr-1 sm:pr-2 custom-scrollbar flex-grow">
                    {/* History */}
                    {messages.map((msg) => (
                        <div
                            key={msg.id}
                            className={`p-3 sm:p-4 rounded-xl border ${msg.sender === 'user'
                                ? 'bg-blue-500/10 border-blue-500/20 ml-4 sm:ml-8'
                                : 'bg-[var(--tangerine)]/10 border-[var(--tangerine)]/20 mr-4 sm:mr-8'
                                }`}
                        >
                            <p className={`text-[10px] font-black mb-1 uppercase tracking-widest ${msg.sender === 'user' ? 'text-blue-400 text-right' : 'text-[var(--tangerine)]'
                                }`}>
                                {msg.sender === 'user' ? 'You' : coachName}
                            </p>
                            <p className="text-[var(--text-primary)] text-base sm:text-lg leading-relaxed">{msg.text}</p>
                        </div>
                    ))}

                    {/* Current User Transcript (Live) */}
                    {localTranscript && (
                        <div className="p-3 sm:p-4 rounded-xl border bg-blue-500/10 border-blue-500/20 ml-4 sm:ml-8 transition-all duration-300">
                            <p className="text-[10px] font-black text-blue-400 mb-1 uppercase tracking-widest text-right">You</p>
                            <p className="text-[var(--text-primary)] text-base sm:text-lg leading-relaxed">{localTranscript}</p>
                        </div>
                    )}

                    <div ref={messagesEndRef} />
                </div>
            </div>

            {/* Controls */}
            <div className="relative flex items-center justify-center w-full">
                <button
                    onClick={toggleRecording}
                    disabled={!isConnected}
                    className={`w-20 h-20 shrink-0 rounded-full flex items-center justify-center transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-offset-2 z-10 ${!isConnected
                        ? 'bg-[var(--off-white)] text-[var(--text-secondary)]/40 cursor-not-allowed'
                        : isRecording
                            ? 'bg-red-500 text-white shadow-lg shadow-red-500/20 animate-pulse'
                            : 'bg-accent-green text-white shadow-lg shadow-green-500/20'
                        }`}
                >
                    {isRecording ? (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z" clipRule="evenodd" />
                        </svg>
                    ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clipRule="evenodd" />
                        </svg>
                    )}
                </button>

                {/* Stop Response Button - Positioned absolutely to not affect centering */}
                {isSpeaking && (
                    <div className="absolute left-[calc(50%+4rem)] sm:left-[calc(50%+5rem)]">
                        <button
                            onClick={stopPlayback}
                            className="w-12 h-12 rounded-full bg-red-500/10 border border-red-500/20 text-red-500 flex items-center justify-center transition-all duration-300 hover:bg-red-500/20 animate-fade-in"
                            title="Stop Speaking"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                )}
            </div>

            {error && (
                <div className="mt-4 text-red-500 text-sm bg-red-500/10 px-4 py-2 rounded-md">
                    Error: {error}
                </div>
            )}

            <p className="mt-6 text-xs text-[var(--text-secondary)]/60">
                {isRecording ? "Tap to stop" : "Tap microphone to start speaking"}
            </p>
        </div>
    );
};

export default VoiceChat;
